import {
  AS400_APPROVAL_SYNC_FAILED_TOOLTIP,
  AS400_SYNC_FAILED_TOOLTIP,
  AS400_SYNC_PENDING_TOOLTIP,
  AS400_SYNC_RESOLVED_TOOLTIP,
  AS400_VOID_SKIPPED_AFTER_RESOLVED_TOOLTIP,
  AS400_VOID_SKIPPED_TOOLTIP,
  INVOICE_STATUS_TO_AS400_SYNC_TOOLTIP,
} from "../constants/invoiceAs400Sync"
import {
  type InvoiceAs400SyncIndicatorConfig,
  InvoiceAs400SyncIndicatorVariant,
  type InvoiceAs400SyncState,
  type InvoiceAs400SyncStatusField,
} from "../types/invoiceAs400Sync"

import {
  type Invoice,
  InvoiceAs400SyncKind,
  InvoiceStatus,
  As400TransactionSyncStatus as SyncStatus,
} from "@/graphql/codegen/graphql"

// The sync an invoice status is waiting on. Approved is handled apart: its approval sync has its own label.
const INVOICE_STATUS_TO_SYNC_FIELD: Partial<Record<InvoiceStatus, InvoiceAs400SyncStatusField>> = {
  [InvoiceStatus.Paid]: "as400PaidSyncStatus",
  [InvoiceStatus.Voided]: "as400VoidSyncStatus",
  [InvoiceStatus.Cancelled]: "as400ReversalSyncStatus",
  [InvoiceStatus.PendingApproval]: "as400ReversalSyncStatus",
  [InvoiceStatus.NotApproved]: "as400ReversalSyncStatus",
}

// The syncs that run after an invoice was approved. A failure on any of them stays visible on every status.
const DOWNSTREAM_SYNC_FIELDS: InvoiceAs400SyncStatusField[] = [
  "as400PaidSyncStatus",
  "as400VoidSyncStatus",
  "as400ReversalSyncStatus",
]

// AK-5232: an approved invoice is not fully approved until AS400 confirms the voucher, so the approval sync only
// describes an invoice that is still Approved. Develop leaves a failed one in place after the invoice is cancelled,
// denied or resubmitted (AK-5649 AC3: no warning then).
type InvoiceApproveSync = Pick<Invoice, "status" | "as400ApproveSyncStatus">

export const isApproveSyncInProgress = (invoice: InvoiceApproveSync): boolean =>
  invoice.status === InvoiceStatus.Approved && invoice.as400ApproveSyncStatus === SyncStatus.InProgress

export const isApproveSyncFailed = (invoice: InvoiceApproveSync): boolean =>
  invoice.status === InvoiceStatus.Approved && invoice.as400ApproveSyncStatus === SyncStatus.Failed

// AK-5232 AC2, AK-5383 AC2, AK-5648 AC2, AK-5649 AC2/AC4: every invoice-modifying action waits while any sync runs.
export const isAs400SyncInProgress = (invoice: InvoiceAs400SyncState): boolean =>
  isApproveSyncInProgress(invoice) || DOWNSTREAM_SYNC_FIELDS.some((field) => invoice[field] === SyncStatus.InProgress)

// AK-5649 AC4: a resubmitted invoice cannot be reviewed while the reversal of its approval is still running.
export const isResubmissionReversalInProgress = (
  invoice: Pick<Invoice, "status" | "as400ReversalSyncStatus">
): boolean =>
  invoice.status === InvoiceStatus.PendingApproval && invoice.as400ReversalSyncStatus === SyncStatus.InProgress

const getOwnSyncStatus = (invoice: InvoiceAs400SyncState): SyncStatus | undefined => {
  const field = INVOICE_STATUS_TO_SYNC_FIELD[invoice.status]
  return field ? invoice[field] : undefined
}

type InvoiceAs400ReconciliationState = Pick<
  Invoice,
  "status" | "as400PaidSyncStatus" | "as400VoidSyncStatus" | "as400ReversalSyncStatus"
>

// A Voided invoice whose void never reached AS400 because the payment sync Failed or was reconciled by hand. The void
// stays Not Synced, and the backend accepts a manual reconciliation for exactly this state (AK-7145).
export const isSkippedVoidPendingReconciliation = (invoice: InvoiceAs400ReconciliationState): boolean =>
  invoice.status === InvoiceStatus.Voided &&
  invoice.as400VoidSyncStatus === SyncStatus.NotSynced &&
  (invoice.as400PaidSyncStatus === SyncStatus.Failed || invoice.as400PaidSyncStatus === SyncStatus.ManuallyResolved)

// AK-5648 AC4: a skipped void keeps a red warning.
const getSkippedVoidIndicator = (invoice: InvoiceAs400SyncState): InvoiceAs400SyncIndicatorConfig | undefined => {
  if (!isSkippedVoidPendingReconciliation(invoice)) return undefined
  return {
    variant: InvoiceAs400SyncIndicatorVariant.Failed,
    tooltip:
      invoice.as400PaidSyncStatus === SyncStatus.Failed
        ? AS400_VOID_SKIPPED_TOOLTIP
        : AS400_VOID_SKIPPED_AFTER_RESOLVED_TOOLTIP,
  }
}

// AK-6358 AC #1: Only a payment, void or reversal sync can be reconciled by hand; a failed approval sync means the
// voucher never reached AS400, so there is nothing to reconcile.
type ReconcilableAs400SyncKind = Exclude<InvoiceAs400SyncKind, InvoiceAs400SyncKind.Approve>

// The sync the status depends on is offered first, so the option matches the tooltip on the row (a Paid invoice whose
// payment and reversal both failed reconciles the payment); the others follow, oldest operation first.
const INVOICE_STATUS_TO_AS400_RECONCILIATION_PRIORITY: Partial<Record<InvoiceStatus, ReconcilableAs400SyncKind[]>> = {
  [InvoiceStatus.Paid]: [InvoiceAs400SyncKind.Paid, InvoiceAs400SyncKind.Reversal, InvoiceAs400SyncKind.Void],
  [InvoiceStatus.Voided]: [InvoiceAs400SyncKind.Void, InvoiceAs400SyncKind.Reversal, InvoiceAs400SyncKind.Paid],
}

const DEFAULT_AS400_RECONCILIATION_PRIORITY: ReconcilableAs400SyncKind[] = [
  InvoiceAs400SyncKind.Reversal,
  InvoiceAs400SyncKind.Paid,
  InvoiceAs400SyncKind.Void,
]

const AS400_SYNC_KIND_TO_STATUS_FIELD = {
  [InvoiceAs400SyncKind.Paid]: "as400PaidSyncStatus",
  [InvoiceAs400SyncKind.Void]: "as400VoidSyncStatus",
  [InvoiceAs400SyncKind.Reversal]: "as400ReversalSyncStatus",
} as const

// AK-7183: a skipped void counts as reconcilable although its sync is Not Synced, not Failed.
const isAs400SyncReconcilable = (invoice: InvoiceAs400ReconciliationState, kind: ReconcilableAs400SyncKind): boolean =>
  invoice[AS400_SYNC_KIND_TO_STATUS_FIELD[kind]] === SyncStatus.Failed ||
  (kind === InvoiceAs400SyncKind.Void && isSkippedVoidPendingReconciliation(invoice))

export const findReconcilableAs400SyncKind = (
  invoice: InvoiceAs400ReconciliationState
): ReconcilableAs400SyncKind | undefined => {
  const priority =
    INVOICE_STATUS_TO_AS400_RECONCILIATION_PRIORITY[invoice.status] ?? DEFAULT_AS400_RECONCILIATION_PRIORITY
  return priority.find((kind) => isAs400SyncReconcilable(invoice, kind))
}

// One indicator per row. A failure outranks a running sync, which outranks a manually reconciled one, so an open
// exception is never hidden. Only the sync the status depends on can show the green icon.
export const getInvoiceAs400SyncIndicatorConfig = (
  invoice: InvoiceAs400SyncState
): InvoiceAs400SyncIndicatorConfig | undefined => {
  const ownSyncStatus = getOwnSyncStatus(invoice)
  const skippedVoidIndicator = getSkippedVoidIndicator(invoice)
  if (skippedVoidIndicator) return skippedVoidIndicator

  if (isApproveSyncFailed(invoice)) {
    return { variant: InvoiceAs400SyncIndicatorVariant.Failed, tooltip: AS400_APPROVAL_SYNC_FAILED_TOOLTIP }
  }

  const statusTooltips = INVOICE_STATUS_TO_AS400_SYNC_TOOLTIP[invoice.status]

  if (ownSyncStatus === SyncStatus.Failed) {
    return {
      variant: InvoiceAs400SyncIndicatorVariant.Failed,
      tooltip: statusTooltips?.[SyncStatus.Failed] ?? AS400_SYNC_FAILED_TOOLTIP,
    }
  }
  if (DOWNSTREAM_SYNC_FIELDS.some((field) => invoice[field] === SyncStatus.Failed)) {
    return { variant: InvoiceAs400SyncIndicatorVariant.Failed, tooltip: AS400_SYNC_FAILED_TOOLTIP }
  }

  if (ownSyncStatus === SyncStatus.InProgress) {
    return {
      variant: InvoiceAs400SyncIndicatorVariant.Pending,
      tooltip: statusTooltips?.[SyncStatus.InProgress] ?? AS400_SYNC_PENDING_TOOLTIP,
    }
  }

  // AK-5383 AC2: once the sync this status depends on succeeded there is no extra icon, even if an older sync on the
  // invoice was reconciled by hand.
  if (ownSyncStatus === SyncStatus.ManuallyResolved) {
    return { variant: InvoiceAs400SyncIndicatorVariant.Resolved, tooltip: AS400_SYNC_RESOLVED_TOOLTIP }
  }

  return undefined
}
